import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { CheckIcon, ExternalLinkIcon, ImageIcon, XIcon } from 'lucide-react';
import { AdminRole, PhotoScanStatus, adminPhotoModerationRequestSchema } from '@qaytim/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DetailShell, DetailSkeleton, FactList, OwnerCard } from '@/components/detail-shell';
import { Field } from '@/components/field';
import { FormDialog } from '@/components/form-dialog';
import { Input } from '@/components/ui/input';
import { Card as DecisionCard } from '@/components/ui/card';
import { useAdminForm } from '@/lib/admin-form';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice, SuccessNotice, WarningNotice } from '@/components/states';
import { LedgerTable } from '@/pages/UserDetailPage';
import { authStore } from '@/lib/auth-store';
import { usePhotoDetail, useModeratePhoto } from '@/lib/admin-queries';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

const DASH = '—';

/**
 * Bitta rasm skani — RASMNING O'ZI bilan.
 *
 * Shu paytgacha panelda rasmni ko'radigan yuza umuman yo'q edi: `photo_scans.storage_key`
 * bazada yotardi, moderatsiya ro'yxati esa faqat holat va sanoqlarni ko'rsatardi. Ya'ni
 * "rasmlarni moderatsiya qilish" ekrani rasmga qaramasdan qaror qabul qilishni taklif qilardi.
 *
 * Havola qisqa muddatli va serverda imzolanadi. Muddati tugasa panel "rasm yo'q" demaydi —
 * u sahifani yangilashni so'raydi, chunki bu ikkisi butunlay boshqa vaziyat.
 */
export function PhotoDetailPage() {
  const { photoScanId } = useParams({ from: '/shell/moderation/photos/$photoScanId' });
  const detail = usePhotoDetail(photoScanId);
  // Ro'yxatlar operatorga ochiq, qaror esa `admin` roliga: rad etish ballni qaytaradi, ya'ni
  // u pul harakati. Haqiqiy chegara serverda — bu faqat ishlamaydigan tugmani yashiradi.
  const isAdmin = authStore.get()?.admin.role === AdminRole.ADMIN;

  const data = detail.data;

  return (
    <DetailShell
      title={strings.detail.photoTitle}
      description={strings.detail.photoSubtitle}
      backTo="/moderation"
      actions={
        data && data.imageUrl !== '' ? (
          <Button variant="outline" size="sm" asChild>
            <a href={data.imageUrl} target="_blank" rel="noreferrer">
              <ExternalLinkIcon aria-hidden="true" />
              {strings.detail.photoOpenFull}
            </a>
          </Button>
        ) : null
      }
    >
      {detail.isError ? <ErrorNotice error={detail.error} /> : null}
      {detail.isPending ? <DetailSkeleton /> : null}

      {data ? (
        <>
          <OwnerCard owner={data.owner} />

          {/*
            Qaror bloki rasmdan OLDIN emas, keyin turishi kerak edi — ammo u ekranning eng
            tepasida, chunki `in_review` dagi skan uchun bu sahifaning yagona maqsadi shu.
            Boshqa holatdagi skan uchun u umuman chizilmaydi.
          */}
          <PhotoDecision photoScanId={photoScanId} status={data.photo.status} visible={isAdmin} />

          <div className="grid gap-6 xl:grid-cols-[minmax(0,24rem)_1fr]">
            <Card>
              <CardHeader>
                <CardTitle>{strings.detail.photoImage}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.imageUrl === '' ? (
                  <WarningNotice>{strings.detail.photoImageMissing}</WarningNotice>
                ) : (
                  /*
                    `object-contain` — kesib emas, sig'dirib: moderator qaror qabul qilayotgan
                    narsa rasmning O'ZI, va kesilgan chekka aynan hal qiluvchi detal bo'lishi
                    mumkin.
                  */
                  <img
                    src={data.imageUrl}
                    alt={strings.detail.photoImage}
                    className="bg-muted max-h-96 w-full rounded-lg object-contain"
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{strings.detail.photoTitle}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge>{data.photo.status}</StatusBadge>
                  <StatusBadge>{data.photo.subject}</StatusBadge>
                  {data.ticket ? (
                    <Button size="sm" variant="ghost" asChild>
                      <Link
                        to="/moderation/tickets/$ticketId"
                        params={{ ticketId: data.ticket.id }}
                      >
                        {strings.detail.linkedTicket}: {data.ticket.code}
                      </Link>
                    </Button>
                  ) : null}
                </div>

                <FactList
                  facts={[
                    [strings.moderation.columnRecognised, data.photo.recognisedItemCount],
                    [strings.moderation.columnItems, data.photo.itemCount],
                    [strings.moderation.columnPoints, data.photo.pointsAwarded],
                    [strings.detail.photoProvider, data.photo.visionProvider ?? DASH],
                    [strings.moderation.columnScannedAt, formatTashkent(data.photo.createdAt)],
                    [
                      strings.detail.photoConfirmedAt,
                      data.confirmedAt ? formatTashkent(data.confirmedAt) : DASH,
                    ],
                    [strings.detail.photoSize, data.sizeBytes ?? DASH],
                    [strings.dlq.columnType, data.contentType ?? DASH],
                  ]}
                />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.photoItems}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.items.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={ImageIcon} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{strings.detail.columnName}</TableHead>
                      {/*
                        AI aytgan nom ALOHIDA ustunda: ikkisining farqi foydalanuvchi natijani
                        qanchalik tuzatganini ko'rsatadi, va aynan shu tanish aniqligining
                        o'lchovi.
                      */}
                      <TableHead>{strings.detail.columnOriginalName}</TableHead>
                      <TableHead className="text-right">
                        {strings.detail.columnConfidence}
                      </TableHead>
                      <TableHead>{strings.detail.columnConfirmed}</TableHead>
                      <TableHead>{strings.detail.columnEdited}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell
                          className={
                            item.edited
                              ? 'text-muted-foreground line-through'
                              : 'text-muted-foreground'
                          }
                        >
                          {item.originalName}
                        </TableCell>
                        <TableCell className="tabular text-right">
                          {item.confidence === null ? DASH : item.confidence.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {item.confirmed ? strings.detail.yes : strings.detail.no}
                        </TableCell>
                        <TableCell>
                          {item.edited ? strings.detail.yes : strings.detail.no}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.ledgerHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.ledger.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={ImageIcon} />
              ) : (
                <LedgerTable rows={data.ledger} />
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </DetailShell>
  );
}

/**
 * Rasm bo'yicha qaror — ikkita tugma, ikkalasi ham sabab so'raydi.
 *
 * Blok faqat `in_review` dagi skan uchun chiziladi. Boshqa holatda server baribir rad etadi,
 * va ishlamaydigan tugmani ko'rsatib turish operatorni bosishga, keyin xatoni o'qishga
 * majbur qilardi.
 */
function PhotoDecision({
  photoScanId,
  status,
  visible,
}: {
  photoScanId: string;
  status: string;
  visible: boolean;
}) {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const mutation = useModeratePhoto(photoScanId);
  const form = useAdminForm({
    schema: adminPhotoModerationRequestSchema,
    defaultValues: { action: 'approve', reason: '' },
  });

  if (!visible || status !== PhotoScanStatus.IN_REVIEW) return null;

  const submit = form.handleSubmit((values) => {
    if (action === null) return;
    mutation.mutate(
      { action, reason: values.reason },
      {
        onSuccess: () => {
          setAction(null);
          form.reset({ action: 'approve', reason: '' });
        },
        onError: form.applyServerError,
      },
    );
  });

  const rejecting = action === 'reject';

  return (
    <DecisionCard className="border-warning/40">
      <CardHeader>
        <CardTitle>{strings.photoModeration.heading}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <FormDialog
          open={action !== null}
          onOpenChange={(next) => {
            if (!next) setAction(null);
          }}
          trigger={
            <Button size="sm" onClick={() => setAction('approve')}>
              <CheckIcon aria-hidden="true" />
              {strings.photoModeration.approve}
            </Button>
          }
          title={
            rejecting ? strings.photoModeration.rejectTitle : strings.photoModeration.approveTitle
          }
          description={
            rejecting ? strings.photoModeration.rejectHint : strings.photoModeration.approveHint
          }
          submitLabel={strings.photoModeration.heading}
          submitting={mutation.isPending}
          onSubmit={submit}
          error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : undefined}
        >
          <Field
            label={strings.userActions.reason}
            hint={strings.userActions.reasonHint}
            error={form.formState.errors.reason?.message}
          >
            <Input {...form.register('reason')} />
          </Field>
        </FormDialog>

        <Button size="sm" variant="destructive" onClick={() => setAction('reject')}>
          <XIcon aria-hidden="true" />
          {strings.photoModeration.reject}
        </Button>

        <span className="text-muted-foreground text-xs">{strings.photoModeration.subtitle}</span>

        {mutation.isSuccess && action === null ? (
          <SuccessNotice>
            {mutation.data.photo.status === PhotoScanStatus.REJECTED
              ? strings.photoModeration.rejected
              : strings.photoModeration.approved}
          </SuccessNotice>
        ) : null}
      </CardContent>
    </DecisionCard>
  );
}
